import './StructureNavigator.css';

import { Box, Button, Grid } from '@material-ui/core';
import { MerchantStructure } from '@nemuru-eng/js-api-client';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { generatePath } from 'react-router-dom';

import { channelHasAgentMatchingSearchTerm } from '@/components/modules/MerchantDetail/MerchantStructure/components/_shared/maps/agentSearchTermMatchMapper';
import { AgentFooter } from '@/components/modules/MerchantDetail/MerchantStructure/components/AgentList/AgentFooter';
import { AgentListWithPagination } from '@/components/modules/MerchantDetail/MerchantStructure/components/AgentList/AgentListWithPagination/AgentListWithPagination';
import { ChannelFooter } from '@/components/modules/MerchantDetail/MerchantStructure/components/ChannelList/ChannelFooter';
import { ChannelListWithPagination } from '@/components/modules/MerchantDetail/MerchantStructure/components/ChannelList/ChannelListWithPagination/ChannelListWithPagination';
import { useSearchMerchantChannel } from '@/components/modules/MerchantDetail/MerchantStructure/components/ChannelList/ChannelListWithPagination/hooks/useSearchMerchantChannel';
import { ChannelHeaderCard } from '@/components/modules/MerchantDetail/MerchantStructure/components/HeaderCard/ChannelHeaderCard';
import { MerchantHeaderCard } from '@/components/modules/MerchantDetail/MerchantStructure/components/HeaderCard/MerchantHeaderCard';
import { UserSearch } from '@/components/modules/MerchantDetail/MerchantStructure/components/UserSearch';
import { CircularLoading } from '@/components/shared/components/CircularLoading';
import { CreateAgentDialog } from '@/components/shared/components/CreateAgentDialog/CreateAgentDialog';
import { CreateChannelDialog } from '@/components/shared/components/CreateChannelDialog/CreateChannelDialog';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useSearch } from '@/components/shared/hooks/useSearch';
import { PAGE_AGENT_ALLOWED_ROLES } from '@/components/views/DynamicPageView/config/layoutScheme/constants/PageAgentAllowedRoles';
import { PAGE_CHANNEL_ALLOWED_ROLES } from '@/components/views/DynamicPageView/config/layoutScheme/constants/PageChannelAllowedRoles';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { useRoles } from '@/hooks/useRoles';
import { useScreen } from '@/hooks/useScreen';

interface StructureNavigatorProps {
  merchantStructure: MerchantStructure;
  loading: boolean;
  editModeAllowed?: boolean;
  hideChannelList?: boolean;
}

export const StructureNavigator = ({
  merchantStructure,
  loading,
  editModeAllowed = false,
  hideChannelList = false,
}: StructureNavigatorProps) => {
  const { isMobile } = useScreen();
  const { merchantId, channelId } = useParams();

  const { opened, setOpened, toggleDialog } = useDialog();
  const { roleExistsInProfile } = useRoles();

  const [action, setAction] = useState(undefined as StructureNavigatorAction | undefined);
  const [searchParams, setSearchParams] = useState<{ agent: string; channel: string }>({
    agent: '',
    channel: '',
  });

  const merchantChannels = merchantStructure.merchant.channels;
  const isChannelRedirectAllowed = roleExistsInProfile(PAGE_CHANNEL_ALLOWED_ROLES);
  const isAgentRedirectAllowed = roleExistsInProfile(PAGE_AGENT_ALLOWED_ROLES);

  const { searchAgent, searchChannel } = useSearchMerchantChannel({ merchantChannels, setSearchParams, channelId });

  const {
    onSearch: onSearchChannel,
    onSelect: onSelectChannel,
    selectedElement: selectedChannel,
    filteredData: filteredChannels,
    onEnter: selectNextChannelOnEnter,
  } = useSearch({
    searchFunction: searchChannel,
    data: merchantStructure.merchant.channels,
    onClear: () => setSearchParams((s) => ({ ...s, channel: '' })),
  });

  const agentData = useMemo(
    () => merchantStructure.merchant.channels.flatMap((channel) => channel.agents),
    [merchantStructure.merchant.channels],
  );

  const {
    onSearch: onSearchAgent,
    onSelect: onSelectAgent,
    selectedElement: selectedAgent,
    onEnter: selectNextAgentOnEnter,
  } = useSearch({
    searchFunction: searchAgent,
    data: agentData,
    onClear: () => setSearchParams((s) => ({ ...s, agent: '' })),
  });

  const filteredChannelsByAgentSearch = useMemo(() => {
    if (!searchParams.agent) return filteredChannels;
    return merchantChannels.filter((channel) =>
      channelHasAgentMatchingSearchTerm(channel, searchParams.agent),
    );
  }, [searchParams.agent, merchantChannels, filteredChannels]);

  const isAgentSearchWithNoResults = !!searchParams.agent && filteredChannelsByAgentSearch.length === 0;

  useEffect(() => {
    if (channelId) {
      onSelectChannel(filteredChannels.findIndex((channel) => channel.id === channelId));
      return;
    }

    // Keeps the selected channel if it makes sense
    if (
      selectedChannel !== undefined &&
      !!filteredChannelsByAgentSearch.find((channel) => channel.id === selectedChannel.id)
    )
      return onSelectChannel(filteredChannels.findIndex((channel) => channel.id === selectedChannel.id));

    if (!searchParams.agent) return onSelectChannel(0);
    if (filteredChannelsByAgentSearch.length > 0)
      onSelectChannel(filteredChannels.indexOf(filteredChannelsByAgentSearch[0]));
  }, [filteredChannelsByAgentSearch]);

  useEffect(() => {
    if (!searchParams.agent) return onSelectAgent(undefined);
    if (!selectedAgent || channelId) return;
    const channelWithAgent = merchantChannels.find((channel) => channel.agents.some((a) => a.id === selectedAgent.id));
    if (!channelWithAgent) return;
    onSelectChannel(filteredChannels.indexOf(channelWithAgent));
  }, [selectedAgent, searchParams.agent]);

  const handleAction = (action: StructureNavigatorAction) => {
    setAction(action);
    toggleDialog();
  };

  if (!merchantId) return null;

  const redirectToChannelPath = !selectedChannel
    ? undefined
    : generatePath(DYNAMIC_PAGE_PATHS.CHANNEL, {
        merchantId,
        channelId: selectedChannel.id,
      });

  if (!selectedChannel && !searchParams.channel) {
    return (
      <>
        <PendingDataWarning tId={'THERE_ARE_NO_CHANNEL_YET'} />
        {editModeAllowed && (
          <>
            <Box mt={2}>
              <Button
                size={'small'}
                variant={'text'}
                onClick={() => handleAction(StructureNavigatorAction.ADD_CHANNEL)}
                startIcon={<i className="material-icons-outlined">add</i>}
              >
                <Font variant={'body2'} color={'textSecondary'}>
                  <Text tId={'ADD_CHANNEL'} />
                </Font>
              </Button>
            </Box>
            {action === StructureNavigatorAction.ADD_CHANNEL && (
              <CreateChannelDialog
                merchantId={merchantId}
                opened={opened}
                setOpened={setOpened}
                toggleDialog={toggleDialog}
              />
            )}
          </>
        )}
      </>
    );
  }

  return (
    <Box>
      <Grid container spacing={isMobile ? 0 : 4}>
        {!hideChannelList && (
          <Grid item xs={12} lg={6}>
            {!isMobile && (
              <Box pl={1} pr={2} mb={2}>
                <MerchantHeaderCard merchantStructure={merchantStructure} />
              </Box>
            )}
            <CustomPaper
              fill="grey.light"
              style={{ position: 'relative', padding: isMobile ? 8 : 16, height: isMobile ? 'inherit' : '404px' }}
            >
              {loading && <AbsoluteLoading />}
              <Box
                position={'relative'}
                height={'100%'}
                style={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.3s ease-in-out' }}
              >
                <ChannelListWithPagination
                  agentSearchTerm={searchParams.agent}
                  channelList={filteredChannelsByAgentSearch}
                  merchantId={merchantStructure.merchant.id}
                  selectedChannelKey={
                    selectedChannel ? filteredChannelsByAgentSearch.indexOf(selectedChannel) : undefined
                  }
                  selectChannel={onSelectChannel}
                  agentFoundBySearch={selectedAgent}
                  isChannelRedirectAllowed={isChannelRedirectAllowed}
                />
                <Box className={'user-search'} maxWidth={'58%'} px={1}>
                  <UserSearch
                    userType={'channel'}
                    onSearch={onSearchChannel}
                    onKeyDown={selectNextChannelOnEnter}
                    hideEndAdornment
                  />
                </Box>
              </Box>
            </CustomPaper>
            {!isAgentSearchWithNoResults && (
              <ChannelFooter
                editModeAllowed={editModeAllowed}
                onAddChannel={() => handleAction(StructureNavigatorAction.ADD_CHANNEL)}
                merchantStructure={merchantStructure}
              />
            )}
          </Grid>
        )}
        <Grid item xs={12} lg={6}>
          {!isMobile && selectedChannel ? (
            <Box pl={1} pr={2} mb={2}>
              <ChannelHeaderCard
                channel={selectedChannel}
                merchantStructure={merchantStructure}
                redirectPath={hideChannelList || !isChannelRedirectAllowed ? undefined : redirectToChannelPath}
                editModeAllowed={editModeAllowed}
              />
            </Box>
          ) : (
            <Box height={68} />
          )}
          <CustomPaper fill="grey.light" style={{ position: 'relative', padding: isMobile ? 8 : 16, height: '404px' }}>
            {loading && <AbsoluteLoading />}
            <Box
              position={'relative'}
              height={'100%'}
              style={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.3s ease-in-out' }}
            >
              <AgentListWithPagination
                agentSearchTerm={searchParams.agent}
                selectedChannel={selectedChannel}
                merchantStructure={merchantStructure}
                agentFoundBySearch={selectedAgent}
                editModeAllowed={editModeAllowed}
                isAgentRedirectAllowed={isAgentRedirectAllowed}
              />
              <Box className={'user-search'} maxWidth={'58%'} px={1}>
                <UserSearch userType={'agent'} onSearch={onSearchAgent} onKeyDown={selectNextAgentOnEnter} />
              </Box>
            </Box>
          </CustomPaper>
          {!isAgentSearchWithNoResults && (
            <AgentFooter
              editModeAllowed={editModeAllowed}
              onAddAgent={() => handleAction(StructureNavigatorAction.CREATE_AGENT)}
              selectedChannel={selectedChannel}
            />
          )}
        </Grid>
      </Grid>
      {action === StructureNavigatorAction.ADD_CHANNEL && (
        <CreateChannelDialog
          merchantId={merchantId}
          opened={opened}
          setOpened={setOpened}
          toggleDialog={toggleDialog}
        />
      )}
      {action === StructureNavigatorAction.CREATE_AGENT && selectedChannel && (
        <CreateAgentDialog
          merchantId={merchantId}
          opened={opened}
          setOpened={setOpened}
          toggleDialog={toggleDialog}
          currentChannel={selectedChannel}
        />
      )}
    </Box>
  );
};

const AbsoluteLoading = () => {
  return (
    <Box
      top={0}
      left={0}
      position={'absolute'}
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      height={'100%'}
      width={'100%'}
    >
      <CircularLoading />
    </Box>
  );
};

export enum StructureNavigatorAction {
  CREATE_AGENT = 'CREATE_AGENT',
  ADD_CHANNEL = 'ADD_CHANNEL',
}
